import React, {
  ReactElement,
  useRef,
  useEffect,
  useReducer,
  useState
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ngnisActions } from '@detox/actions';
import { Modal, Spacing } from '@dls/web';
import { Form } from '@wec-core/form-engine';
import CONSTANTS from '../../../constants/checkout';
import useTranslate from '../../../hooks/useTranslation';

import {
  getPostalCode,
  getAddNewPostalSubmit,
  getFormDataMapping,
  FORM_MAPPING_ACTIONS,
  getFloorAndUnitField,
  floorValidation,
  unitValidation,
  handleOnFloorNumberChange
} from '../helper';
import { getResetPostalValues } from './helpers';
import {
  TObject,
  Address,
  AddressStatus,
  SelectionObject
} from '../../../types/registrationCheckout';
import {
  checkoutFlowReducer as addNewAddressReducer,
  checkoutInitialState as addNewAddressInitialState
} from '../checkoutFlowState';
import { usePostalCodeHelper } from '../hooks/usePostalCodeHelper';
import { KeyValue } from '../../../types/common.types';
import { confirmSavedNewAddress } from '../../../reducers/fulfillment/addressInfo';

interface TProps {
  isOpen: boolean;
  closeAddressModal: () => void;
  keepAddress: boolean;
  isNGNISAddressFetchEnabled: boolean;
}

const AddNewAddress: React.FC<TProps> = ({
  isOpen,
  closeAddressModal,
  keepAddress,
  isNGNISAddressFetchEnabled
}): ReactElement => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const postalCode = getPostalCode(t);
  const additionalAfterPostal = {
    submit: getAddNewPostalSubmit(t, keepAddress)
  };
  const { addressInfo, addNewAddressData, ngnis } = useSelector(
    (state: TObject) => ({
      addressInfo: state.fulfillment?.addressInfo,
      addNewAddressData: state.checkout?.addNewAddressData,
      ngnis: state.ngnis
    })
  );
  const previousPostal = useRef<KeyValue>(addressInfo?.address?.postcode);
  const enableFormReinitialize = useRef<boolean>(true);
  const loadSavedAddress = useRef<boolean>(keepAddress);
  const [addAddressFormState, setAddAddressFormState] = useState<KeyValue>();
  const [formDataValues, setFormValues] = useState<KeyValue>();

  const [formState, formDispatch] = useReducer(
    addNewAddressReducer,
    addNewAddressInitialState
  );
  const {
    setPostalCodeGroup,
    postalSearchValidation,
    handlePostalSearchEvent,
    togglePostalAddressDetail
  } = usePostalCodeHelper(t, formDispatch, dispatch);

  const formFieldsData = {
    postal: postalCode,
    postalAddress: null,
    submit: null
  };
  const initialValues = {
    postalCode: '',
    floorNumber: '',
    unitNumber: '',
    blkNumber: '',
    unitDetail: '',
    addressCheck: false
  };

  const onAddressCheckChange = (changedData: SelectionObject): void => {
    togglePostalAddressDetail(
      changedData.value,
      addressInfo.address,
      enableFormReinitialize,
      isNGNISAddressFetchEnabled,
      addressInfo?.havingMoreAddress
    );
  };

  const closeAndResetAddress = (): void => {
    if (!keepAddress) {
      dispatch({ type: CONSTANTS.RESET_ADDRESS_DATA });
    }

    closeAddressModal();
  };

  const modalConfig = {
    visible: isOpen,
    onClose: closeAndResetAddress,
    bgColor: 'white',
    backdropClosable: false
  };

  const formattedFormFieldsData = getFormDataMapping(
    formState.formInputsMapping
  );

  const handlePostalSearch = (postalCode: string) => {
    handlePostalSearchEvent(
      postalCode,
      addressInfo,
      false,
      isNGNISAddressFetchEnabled
    );
  };

  const setPostalCodeGroupData = (
    address: Address,
    status: AddressStatus,
    havingMoreAddress: boolean
  ): void => {
    formDispatch({
      type: FORM_MAPPING_ACTIONS.SET_VALUES_CHANGE_ON_DEMAND,
      payload: []
    });

    if (address) {
      setPostalCodeGroup(
        address,
        status,
        addNewAddressData,
        initialValues,
        additionalAfterPostal,
        enableFormReinitialize,
        getResetPostalValues(),
        isNGNISAddressFetchEnabled,
        havingMoreAddress
      );

      if (previousPostal.current !== addressInfo?.address?.postcode) {
        previousPostal.current = addressInfo?.address?.postcode;
        formDispatch({
          type: FORM_MAPPING_ACTIONS.SET_VALUES_CHANGE_ON_DEMAND,
          payload: getFloorAndUnitField()
        });
      }
    }
  };

  const postalCodeValidation = (code = '') => {
    return postalSearchValidation(
      code,
      addressInfo,
      enableFormReinitialize,
      !keepAddress,
      isNGNISAddressFetchEnabled
    );
  };

  const setOverAllFormState = (): void => {
    const payload = {
      formInputsMapping: { ...formFieldsData },
      formValues: { ...initialValues, ...addNewAddressData }
    };
    formDispatch({
      type: FORM_MAPPING_ACTIONS.SET_OVERALL_FORM_STATE,
      payload
    });
  };

  useEffect(() => {
    setOverAllFormState();
  }, []);

  useEffect(() => {
    const { address, savedAddress, status, havingMoreAddress } = addressInfo;

    const loadedAddress = loadSavedAddress.current
      ? { isLandedProperty: address?.isLandedProperty, ...savedAddress }
      : address;

    const loadedStatus: AddressStatus = loadSavedAddress.current
      ? 'SUCCEEDED'
      : status;

    setPostalCodeGroupData(loadedAddress, loadedStatus, havingMoreAddress);
    loadSavedAddress.current = false;
  }, [addressInfo]);

  useEffect(() => {
    if (ngnis?.validateAddress === undefined) return;
    const apiAddress = ngnis?.validateAddress?.submittedGeographicAddress;
    if (
      ngnis?.validateAddress &&
      ngnis?.validateAddress?.validationResult === 'success'
    ) {
      const { streetName, buildingName } = addressInfo?.address;
      const physicalAddress = {
        floorNumber: apiAddress?.levelNumber,
        originalUnitNumber: apiAddress?.subUnitNumber,
        blockOrHouseNum: apiAddress?.buildingNo,
        buildingNo: apiAddress?.buildingNo,
        buildingName: buildingName,
        streetName: streetName,
        unitNumber: apiAddress?.subUnitNumber,
        postcode: apiAddress?.postcode,
        newAddressSubTitle: t('CO_EN_ADD_ADDRESS_NEW'),
        floor: apiAddress?.levelNumber
      };
      dispatch({
        type: CONSTANTS.SET_ADDRESS_FORM_DATA,
        payload: formDataValues
      });
      dispatch({
        type: CONSTANTS.UPDATE_DELIVERY_ADDRESSES,
        payload: { physicalAddress }
      });

      dispatch({
        type: CONSTANTS.UPDATE_ADDRESS_INFO,
        payload: { physicalAddress }
      });
      confirmSavedNewAddress();
      closeAddressModal();
    } else {
      const isLandedProperty = addressInfo?.address?.isLandedProperty;
      formDispatch({
        type: FORM_MAPPING_ACTIONS.SET_VALUES_CHANGE_ON_DEMAND,
        payload: [
          {
            field: isLandedProperty ? 'blkNumber' : 'unitDetail',
            value: '',
            shouldValidate: true
          }
        ]
      });
      dispatch({
        type: CONSTANTS.UPDATE_ADDRESS_INFO,
        payload: null
      });
    }
  }, [ngnis?.validateAddress]);

  const addressFloorValidation = (floor: string) => {
    const floorValue = floor.length === 1 ? '0' + floor : floor;
    return floorValidation(floorValue, addressInfo?.validFloors);
  };

  const addressUnitValidation = (unit: string) => {
    const floorNumber = addAddressFormState?.['floorNumber'] || '';
    const floorValue =
      floorNumber.length === 1 ? '0' + floorNumber : floorNumber;
    const unitValue = unit.length === 1 ? '0' + unit : unit;
    return unitValidation(unitValue, addressInfo?.validFloors, floorValue);
  };

  const unitFlrValidation = (unitFlr: string) => {
    if (ngnis?.validateAddress?.validationResult === 'fail') {
      return { valid: false, message: t('CC_E_API_POSTAL_UNIT') as string };
    }
  };

  const _callBacks = {
    onFloorNumberChange: handleOnFloorNumberChange,
    floorValidation: addressFloorValidation,
    unitValidation: addressUnitValidation,
    // unitNumberValidation: unitFlrValidation,
    postalCodeValidation: postalCodeValidation,
    rightIconLinkClicked: handlePostalSearch,
    onAddressCheckChange
  };

  const _onValueChanged = (values: KeyValue): void => {
    setAddAddressFormState(values);
  };

  const _onSubmit = values => {
    const {
      postcode,
      streetName,
      blockOrHouseNum,
      buildingNo,
      buildingName
    } = addressInfo.address;

    const { floorNumber, unitNumber, unitDetail, blkNumber } = values;
    const unitInfo =
      floorNumber && unitNumber ? `#${floorNumber}-${unitNumber}` : '';

    if (isNGNISAddressFetchEnabled) {
      setFormValues(values);
      let levelNo, unitNo;
      const landedProperty = addressInfo?.address?.isLandedProperty;
      if (unitDetail) {
        const arr = unitDetail.split('-');
        levelNo = arr[0];
        unitNo = arr[1];
      }
      const validateAddressOptions = {
        postcode: postcode,
        buildingNo: addressInfo?.havingMoreAddress ? blkNumber : buildingNo,
        streetName: streetName,
        ...(!landedProperty && {
          ...(levelNo && { levelNumber: levelNo }),
          ...(unitNo && { subUnitNumber: unitNo })
        })
      };
      dispatch({
        type: CONSTANTS.SET_ADDRESS_FORM_DATA,
        payload: values
      });
      dispatch(ngnisActions.validateNgnisAddress(validateAddressOptions));
    } else {
      const physicalAddress = {
        floorNumber,
        originalUnitNumber: unitNumber,
        blockOrHouseNum: blockOrHouseNum || buildingNo,
        buildingName,
        streetName,
        unitNumber: unitInfo,
        postcode,
        newAddressSubTitle: t('CO_EN_ADD_ADDRESS_NEW')
      };

      dispatch({
        type: CONSTANTS.SET_ADDRESS_FORM_DATA,
        payload: values
      });
      dispatch({
        type: CONSTANTS.UPDATE_DELIVERY_ADDRESSES,
        payload: { physicalAddress }
      });

      dispatch({
        type: CONSTANTS.UPDATE_ADDRESS_INFO,
        payload: { physicalAddress }
      });
      confirmSavedNewAddress();
      closeAddressModal();
    }
  };

  const formData = {};
  return (
    <Modal title={t('CO_EN_ADD_ADDRESS_TITLE')} {...modalConfig}>
      <Modal.Content>
        {formattedFormFieldsData && formattedFormFieldsData.length > 0 ? (
          <Spacing top={2}>
            <Form
              stackSpacing={2}
              enableReinitialize={formState.enableReinitialize}
              formFieldsConfig={formattedFormFieldsData}
              valuesChangeOnDemand={formState.valuesChangeOnDemand}
              initialValues={formState.formValues}
              data={formData}
              callbacks={_callBacks}
              onValueChange={_onValueChanged}
              onSubmit={_onSubmit}
            />
          </Spacing>
        ) : null}
      </Modal.Content>
    </Modal>
  );
};

export default AddNewAddress;
